
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/components/ui/use-toast';
import { useBaseMfaState } from './useBaseMfaState';
import { useMfaFactors } from './useMfaFactors';
import { handleMfaError } from '../types';

/**
 * Hook for MFA unenrollment operations
 * 
 * Provides functionality to remove MFA from user accounts
 * 
 * @returns MFA unenrollment function
 */
export function useMfaUnenrollment() {
  const { setIsLoading } = useBaseMfaState();
  const { fetchMfaFactors } = useMfaFactors();

  /**
   * Remove MFA from a user account
   * 
   * This function:
   * 1. Calls the auth provider to unenroll the specified factor
   * 2. Removes the factor from our database
   * 3. Updates the enrolled factors list on success
   * 4. Handles errors with user feedback
   * 
   * @param factorId - ID of the factor to remove
   * @returns Promise resolving to boolean indicating success
   */
  const unenrollMfa = async (factorId: string): Promise<boolean> => {
    try {
      setIsLoading(true);
      
      // Get current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User not authenticated');
      }
      
      const { error } = await supabase.auth.mfa.unenroll({ factorId });
      
      if (error) throw error;
      
      // Remove from our database
      const { error: dbError } = await supabase
        .from('mfa_factors')
        .delete()
        .eq('id', factorId)
        .eq('user_id', user.id);
      
      if (dbError) {
        console.warn('Failed to remove MFA factor from database:', dbError);
      }
      
      // Update enrolled factors after successful unenrollment
      await fetchMfaFactors();
      
      toast({
        title: "MFA Disabled",
        description: "Two-factor authentication has been successfully disabled.",
      });
      
      return true;
    } catch (error) {
      const mfaError = handleMfaError(error);
      console.error('MFA unenrollment failed:', mfaError);
      toast({
        title: "MFA Disable Failed",
        description: mfaError.message || "An unknown error occurred",
        variant: "destructive"
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    unenrollMfa
  };
}
